import styled, { css } from 'styled-components'
import { HTMLAttributes } from 'react'
import { shadowMaterialList } from '@styles/theme-const'
import { ThemeColors } from 'src/shared/ui/consts-styles'
import InputMask, { Props as InputMaskProps } from 'react-input-mask'

interface InputBaseProps extends HTMLAttributes<HTMLInputElement> {
    isValid: boolean
    placeholderRaised?: string
}

export const InputBase = styled.input<InputBaseProps>`
    width: 100%;
    padding: 0.7rem 1rem;
    border: 1px solid ${ThemeColors.accentGreen};
    color: ${ThemeColors.getGrayColor(1)};
    box-sizing: border-box;
    outline: none;
    -webkit-appearance: none;
    border-radius: 7px;
    transition: all 0.35s ease;
    font: inherit;

    ${(props) => (props.isValid ? validatedInput : notValidatedInput)}
    ::placeholder {
        color: ${ThemeColors.getGrayColor(0.4)};
    }

    &:focus {
        color: ${ThemeColors.getGrayColor(1)};
        box-shadow: ${shadowMaterialList.shadowKitHover};

        ::placeholder {
            transition: opacity 0.45s ease;
            color: ${ThemeColors.getGrayColor(0.4)};
        }
    }
`
export const Label = styled.label`
    position: absolute;
    pointer-events: none;
    font-size: 1rem;
    font-weight: normal;
    left: 1.2rem;
    top: 0.875rem;
    transition: 0.2s ease all;
    color: ${ThemeColors.getGrayColor(0.4)};
`
export const RadioGroupLabelStyled = styled.label`
    position: absolute;
    pointer-events: none;
    transition: 0.2s ease all;
    color: ${ThemeColors.getGrayColor(0.4)};
    top: 0;
    left: 1rem;
    font-size: 0.75rem;
`

interface InputPlacholderLabelProps extends HTMLAttributes<HTMLInputElement> {
    isValid: boolean
}

export const InputPlacholderLabel = styled.input<InputPlacholderLabelProps>`
    width: 100%;
    padding: 1.1rem 0.844rem 0.544rem 0.844rem;
    border: 1px solid ${ThemeColors.accentGreen};
    color: ${ThemeColors.getGrayColor(1)};
    box-sizing: border-box;
    outline: none;
    border-radius: 7px;
    -webkit-appearance: none;
    transition: all 0.35s ease;
    font: inherit;
    ${(props) => (props.isValid ? validatedInput : notValidatedInput)};
`

export const InputPlaceholderLabelWrapper = styled.div<{ isValid: boolean }>`
    & input {
        width: 100%;
        padding: 1.1rem 0.844rem 0.544rem 0.844rem;
        border: 1px solid ${ThemeColors.accentGreen};
        color: ${ThemeColors.getGrayColor(1)};
        box-sizing: border-box;
        outline: none;
        border-radius: 7px;
        -webkit-appearance: none;
        transition: all 0.35s ease;
        font: inherit;
        ${(props) => (props.isValid ? validatedInput : notValidatedInput)};
    }
`

export const Container = styled.div`
    position: relative;
    display: flex;
    align-items: center;

    & > input:focus ~ label,
    input:not(:focus):valid ~ label {
        top: 0.1rem;
        bottom: 0.5rem;
        left: 1rem;
        font-size: 0.75rem;
        opacity: 1;
        height: 0.9rem;
    }
`

const validatedInput = css`
    border: 1px solid ${ThemeColors.accentGreen};

    :focus {
        border: 1px solid ${ThemeColors.getGreenColor(0.8)};
    }

    background-color: inherit;
`

const validatedDefaultInput = css`
    border: 1px solid ${ThemeColors.getGrayColor(0.1)};
    background-color: inherit;
`

interface StyledMaskProps extends Omit<InputMaskProps, 'children'> {
    isValid: boolean
}

export const StyledInputMask = styled(InputMask)<StyledMaskProps>`
    width: 100%;
    padding: 1.1rem 0.844rem 0.544rem 0.844rem;
    border: 1px solid ${ThemeColors.accentGreen};
    color: ${ThemeColors.getGrayColor(1)};
    box-sizing: border-box;
    outline: none;
    border-radius: 7px;
    -webkit-appearance: none;
    transition: all 0.35s ease;
    font: inherit;
    ${(props) => (props.isValid ? validatedInput : notValidatedInput)}
`
const notValidatedInput = css`
    border: 1px solid ${ThemeColors.accentRed};

    :focus {
        border: 1px solid ${ThemeColors.accentRed};
    }
`
const invalidatedInput = css`
    border: 1px solid ${ThemeColors.accentRed};
`

interface RadioButtonBodyStyledProps {
    isColumn: boolean
}

export const RadioButtonBodyStyled = styled.div<RadioButtonBodyStyledProps>`
    display: flex;
    flex-direction: ${(props) => (props.isColumn ? 'column' : 'row')};
`

interface RadioButtonContainerStyledProps extends HTMLAttributes<HTMLAreaElement> {
    isValid: boolean
    placeholder?: string
}

export const RadioButtonContainerStyled = styled.div<RadioButtonContainerStyledProps>`
    display: flex;
    flex-direction: column;
    ${(props) => !props.isValid && invalidatedInput}
    border-radius: 0.3rem;
    position: relative;
    ${(props) =>
        props.placeholder &&
        css`
            padding: 0.844rem;
        `}
`

interface TextareaBaseProps extends HTMLAttributes<HTMLAreaElement> {
    isValid: boolean
}

export const TextareaBase = styled.textarea<TextareaBaseProps>`
    width: 100%;
    padding: 0.7rem 1rem;
    font-size: 1rem;
    border: 1px solid rgba(50, 62, 52, 0.1);
    color: ${ThemeColors.getGrayColor(0.6)};
    box-sizing: border-box;
    outline: none;
    border-radius: 0.3rem;
    transition: all 0.35s ease;

    ${(props) => (props.isValid ? validatedInput : notValidatedInput)}
    ::placeholder {
        color: ${ThemeColors.getGrayColor(0.4)};
    }

    &:focus {
        color: ${ThemeColors.getGrayColor(0.6)};
        box-shadow: ${shadowMaterialList.shadowKitHover};
        border: 1px solid ${ThemeColors.getGrayColor(0.3)};

        ::placeholder {
            transition: opacity 0.45s ease;
            color: ${ThemeColors.getGrayColor(0.4)};
        }
    }

    resize: none;
`

export const TextareaLabel = styled.label`
    position: absolute;
    pointer-events: none;
    font-size: 1rem;
    left: 1.2rem;
    top: 1.2rem;
    transition: 0.2s ease all;
    color: ${ThemeColors.getGrayColor(0.4)};
`

interface TextareaInputPlacholderLabelProps extends HTMLAttributes<HTMLAreaElement> {
    withotFocus?: boolean
    isValid: boolean
}

export const TextareaInputPlacholderLabel = styled.textarea<TextareaInputPlacholderLabelProps>`
    width: 100%;
    padding: 1.1rem 1rem;
    font-size: 1rem;
    border: 1px solid ${ThemeColors.getGrayColor(0.03)};
    color: ${ThemeColors.getGrayColor(0.6)};
    ${(props) =>
        !props.withotFocus &&
        css`
            :focus {
                border: 1px solid ${ThemeColors.getGrayColor(0.6)};
            }
        `}

    box-sizing: border-box;
    outline: none;
    border-radius: 0.3rem;
    transition: all 0.35s ease;
    height: 6rem;
    ${(props) => (props.isValid ? validatedDefaultInput : notValidatedInput)}
    resize: none;
`

export const TextareaContainer = styled.div`
    position: relative;

    & > textarea:focus ~ label,
    textarea:not(:focus):valid ~ label {
        top: 0.1rem;
        bottom: 0.5rem;
        left: 1rem;
        font-size: 0.75rem;
        opacity: 1;
    }
`
